// CASH1-S1-U1 (docs/build-briefs/CASH1-S1-U1-drawer-screens.md; PRD P1.1; DEC-399): the pure rules behind the cash drawer
// screens. No DOM and no session import, so a plain node:test file proves them directly
// (test/cash1-s1-ui-drawer-model.test.ts) and ui/src/cashDrawer.ts draws from them.
//
// Money is never typed on the floor. A count is a map from a denomination in cents to how many of it are in the drawer, moved
// by one with a stepper, and the amount sent to the server is the sum of that map. Every decision about who sees what resolves a
// PERMISSION id passed in by the caller, never a role name.

export interface Denomination {
  /** The value of one bill or coin, in cents. Also the key of a count. */
  readonly cents: number;
  readonly label: string;
  readonly kind: "bill" | "coin";
}

/** Mirrors src/config/cashDenominations.ts (CASH_DENOMINATIONS_CENTS), largest first. The ui bundle cannot import from src, so a
 * test fails when the two lists differ. */
export const DENOMINATIONS: readonly Denomination[] = [
  { cents: 10000, label: "$100", kind: "bill" },
  { cents: 5000, label: "$50", kind: "bill" },
  { cents: 2000, label: "$20", kind: "bill" },
  { cents: 1000, label: "$10", kind: "bill" },
  { cents: 500, label: "$5", kind: "bill" },
  { cents: 100, label: "$1", kind: "bill" },
  { cents: 50, label: "50c", kind: "coin" },
  { cents: 25, label: "25c", kind: "coin" },
  { cents: 10, label: "10c", kind: "coin" },
  { cents: 5, label: "5c", kind: "coin" },
  { cents: 1, label: "1c", kind: "coin" },
];

/** Mirrors CASH_DRAWER.maxPerDenomination in src/config/cashDrawers.ts, the typo guard on a stepper. */
export const MAX_PER_DENOMINATION = 10_000;

/** Denomination cents (as a string key) to a whole count. A missing key is zero. */
export type DrawerCounts = Readonly<Record<string, number>>;

export function emptyCounts(): DrawerCounts {
  return {};
}

export function countOf(counts: DrawerCounts, cents: number): number {
  return counts[String(cents)] ?? 0;
}

/** One stepper tap. The count never goes below zero or above the per-denomination cap, and an unknown denomination changes
 * nothing. */
export function stepCount(counts: DrawerCounts, cents: number, delta: 1 | -1): DrawerCounts {
  if (!DENOMINATIONS.some((denomination) => denomination.cents === cents)) return counts;
  const next = Math.min(MAX_PER_DENOMINATION, Math.max(0, countOf(counts, cents) + delta));
  if (next === countOf(counts, cents)) return counts;
  return { ...counts, [String(cents)]: next };
}

export function lineCents(counts: DrawerCounts, cents: number): number {
  return countOf(counts, cents) * cents;
}

/** The counted amount: the sum of every denomination's value times its count. */
export function totalCents(counts: DrawerCounts): number {
  return DENOMINATIONS.reduce(
    (sum, denomination) => sum + lineCents(counts, denomination.cents),
    0,
  );
}

/** What the server receives as `denominations`: zero counts dropped. Its sum equals totalCents exactly. */
export function breakdownOf(counts: DrawerCounts): Record<string, number> {
  const out: Record<string, number> = {};
  for (const denomination of DENOMINATIONS) {
    const count = countOf(counts, denomination.cents);
    if (count > 0) out[String(denomination.cents)] = count;
  }
  return out;
}

// ---------------------------------------------------------------------
// The cash setting and who sees the drawer entry
// ---------------------------------------------------------------------

/** The location's cash switch from a response body. CASH1-S2 owns the switch and serves it as `cashEnabled`; until it does the field is
 * missing, and a missing, false or non-boolean value is OFF. Only the boolean true turns it on, never a truthy string. */
export function cashEnabledFrom(body: unknown): boolean {
  if (typeof body !== "object" || body === null) return false;
  return (body as { cashEnabled?: unknown }).cashEnabled === true;
}

/** RCPT1-S5. Whether the open drawer's newest kick did not open it. Only the exact string "did_not_open" says so; a missing or odd
 * value reads as fine, so a server that does not send the field never shows a false alarm. */
export function drawerKickFailedFrom(body: unknown): boolean {
  if (typeof body !== "object" || body === null) return false;
  return (body as { drawerKick?: unknown }).drawerKick === "did_not_open";
}

export interface DrawerEntryInput {
  /** The session's resolved permissions, as a check. */
  has: (permissionId: string) => boolean;
  cashEnabled: boolean;
  /** A drawer is open on this device right now. */
  drawerOpen: boolean;
}

/** The Cash drawer row on the Shift hub. Needs a drawer permission, and the location's cash switch on OR a drawer already open: turning
 * cash off with a drawer open never strands it, because it can still be counted and closed. */
export function drawerEntryVisible(input: DrawerEntryInput): boolean {
  if (!input.has("drawer.open") && !input.has("drawer.close")) return false;
  return input.cashEnabled || input.drawerOpen;
}

// ---------------------------------------------------------------------
// Over and short
// ---------------------------------------------------------------------

export function dollars(cents: number): string {
  const magnitude = Math.abs(cents);
  return `$${Math.trunc(magnitude / 100)}.${String(magnitude % 100).padStart(2, "0")}`;
}

export type OverShortKind = "over" | "short" | "even";

export interface OverShortView {
  kind: OverShortKind;
  /** "Over $7.00", "Short $3.00" or "Even". */
  headline: string;
  /** "+$7.00", "-$3.00" or "$0.00". */
  difference: string;
}

/** The signed variance (counted minus expected) in words. */
export function overShort(varianceCents: number): OverShortView {
  if (varianceCents > 0) {
    return {
      kind: "over",
      headline: `Over ${dollars(varianceCents)}`,
      difference: `+${dollars(varianceCents)}`,
    };
  }
  if (varianceCents < 0) {
    return {
      kind: "short",
      headline: `Short ${dollars(varianceCents)}`,
      difference: `-${dollars(varianceCents)}`,
    };
  }
  return { kind: "even", headline: "Even", difference: "$0.00" };
}

/** The opening float against the standard float, for the line on the drawer menu. */
export function floatLine(openingCents: number, standardCents: number): string {
  const diff = openingCents - standardCents;
  return diff === 0
    ? dollars(openingCents)
    : `${dollars(openingCents)} (${overShort(diff).difference})`;
}

// ---------------------------------------------------------------------
// What a close response means
// ---------------------------------------------------------------------

export interface CloseResponseInput {
  status: number;
  networkError?: boolean;
  body: {
    outcome?: string;
    error?: string;
    message?: string;
  };
  /** The submission carried a picked reason. */
  hadReason: boolean;
}

export type CloseResponseKind =
  | { kind: "closed" }
  | { kind: "recount_required" }
  /** Outside the limit on the committing count: a reason and a manager are needed. */
  | { kind: "needs_approval" }
  | { kind: "error"; message: string };

/** Turns the close route's answer into the next step. The count is blind, so a closer cannot know in advance that the
 * committing count is outside the limit. A 400 on a submission with no reason is the server asking for one (a count the client
 * built is always valid), and a 403 approval_required is the server asking for a manager. Any other refusal is an error. */
export function classifyCloseResponse(input: CloseResponseInput): CloseResponseKind {
  if (input.networkError === true) {
    return { kind: "error", message: input.body.message ?? "Could not reach the server." };
  }
  if (input.status === 200 || input.status === 201) {
    if (input.body.outcome === "closed") return { kind: "closed" };
    if (input.body.outcome === "recount_required") return { kind: "recount_required" };
    return { kind: "error", message: "Something went wrong. Try again." };
  }
  if (input.status === 403 && input.body.error === "approval_required") {
    return { kind: "needs_approval" };
  }
  if (input.status === 400 && !input.hadReason) return { kind: "needs_approval" };
  return { kind: "error", message: input.body.message ?? "Something went wrong. Try again." };
}

/** Title of the count sheet: the first count, or the one recount. */
export function countTitle(recountUsed: boolean): string {
  return recountUsed ? "Recount the cash" : "Close drawer: count the cash";
}

/** A drawer's opened-at as a short clock time, "5:02 PM", in the device's own time zone. */
export function clockTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

// ---------------------------------------------------------------------
// CASH1-S3: paid in, paid out and drop to safe
// ---------------------------------------------------------------------

export type DrawerMoveKind = "paid-in" | "paid-out" | "drop";

export interface DrawerMoveSpec {
  kind: DrawerMoveKind;
  /** The tile and the sheet title. */
  label: string;
  /** The small word on the tile, from the mockup. */
  hint: string;
  /** The permission that shows the tile and is checked by the server. */
  permission: "drawer.paid_in_out" | "drawer.drop";
  /** The action-reasons list the reason is picked from. */
  reasonKind: "drawer_paid_in" | "drawer_paid_out" | "drawer_drop";
  saveLabel: string;
}

/** The drawer menu tiles in order (mockup 5a without Tip payout: cash has no tips, DEC-399 owner ruling). */
export const DRAWER_MOVES: readonly DrawerMoveSpec[] = [
  {
    kind: "paid-in",
    label: "Paid in",
    hint: "Cash added",
    permission: "drawer.paid_in_out",
    reasonKind: "drawer_paid_in",
    saveLabel: "Save paid in",
  },
  {
    kind: "paid-out",
    label: "Paid out",
    hint: "Cash taken",
    permission: "drawer.paid_in_out",
    reasonKind: "drawer_paid_out",
    saveLabel: "Save paid out",
  },
  {
    kind: "drop",
    label: "Drop to safe",
    hint: "Move cash",
    permission: "drawer.drop",
    reasonKind: "drawer_drop",
    saveLabel: "Save drop",
  },
];

/** The tiles this session may use, by permission (never a role). Shown only while a drawer is open. */
export function visibleMoves(
  has: (permissionId: string) => boolean,
  drawerOpen: boolean,
): readonly DrawerMoveSpec[] {
  if (!drawerOpen) return [];
  return DRAWER_MOVES.filter((spec) => has(spec.permission));
}

export interface MoveDraft {
  counts: DrawerCounts;
  reasonId: string | undefined;
}

/** Save is allowed with a picked reason and a counted amount above zero. */
export function canSaveMove(draft: MoveDraft): boolean {
  return draft.reasonId !== undefined && totalCents(draft.counts) > 0;
}

/** The body a move sends: the amount is the sum of the counts, never typed. */
export function moveBody(
  draft: MoveDraft,
  idempotencyKey: string,
): {
  idempotencyKey: string;
  amountCents: number;
  denominations: Record<string, number>;
  reasonId: string;
} | null {
  if (!canSaveMove(draft) || draft.reasonId === undefined) return null;
  return {
    idempotencyKey,
    amountCents: totalCents(draft.counts),
    denominations: breakdownOf(draft.counts),
    reasonId: draft.reasonId,
  };
}

export interface MoveResponseInput {
  status: number;
  networkError?: boolean;
  body: { error?: string; message?: string };
}

export type MoveResponseKind =
  { kind: "saved" } | { kind: "needs_approval" } | { kind: "error"; message: string };

/** Turns a move route's answer into the next step. A 403 approval_required is the server asking for a manager on a large paid out. */
export function classifyMoveResponse(input: MoveResponseInput): MoveResponseKind {
  if (input.networkError === true) {
    return { kind: "error", message: input.body.message ?? "Could not reach the server." };
  }
  if (input.status === 200 || input.status === 201) return { kind: "saved" };
  if (input.status === 403 && input.body.error === "approval_required") {
    return { kind: "needs_approval" };
  }
  return { kind: "error", message: input.body.message ?? "Something went wrong. Try again." };
}
